import { motion } from "framer-motion";
import {
  Plane, CalendarCheck, Layers, Cog, Armchair, Radio, BadgeCheck, ClipboardCheck, ArrowUpRight,
} from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";

const ICONS = [Plane, CalendarCheck, Layers, Cog, Armchair, Radio, BadgeCheck, ClipboardCheck];
const SPANS = [
  "md:col-span-3", "md:col-span-3",
  "md:col-span-2", "md:col-span-2", "md:col-span-2",
  "md:col-span-3", "md:col-span-3",
  "md:col-span-6",
];
const CARD_IMG = "/work4.jpg";

export const Services = () => {
  const { t } = useLanguage();
  return (
    <section id="services" data-testid="services-section" className="py-28 md:py-36 max-w-7xl mx-auto px-6">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
        className="mb-16 md:flex md:items-end md:justify-between gap-10"
      >
        <div>
          <p className="font-mono-tech text-xs tracking-[0.25em] uppercase text-[#60A5FA] mb-6" data-testid="services-overline">
            {t.services.overline}
          </p>
          <h2 className="font-display font-bold tracking-tight leading-[1.05] text-4xl md:text-5xl lg:text-6xl max-w-2xl" data-testid="services-title">
            {t.services.title}
          </h2>
        </div>
        <p className="mt-6 md:mt-0 max-w-sm text-slate-400 text-base leading-relaxed" data-testid="services-sub">
          {t.services.sub}
        </p>
      </motion.div>

      <div className="grid md:grid-cols-6 gap-4">
        {t.services.items.map((s, i) => {
          const Icon = ICONS[i];
          const wide = SPANS[i] === "md:col-span-6";
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
              data-testid={`service-card-${i}`}
              className={`group relative overflow-hidden rounded-xl border border-white/10 bg-[#0F172A]/60 p-7 transition-all duration-500 hover:border-[#2563EB]/60 hover:bg-[#0F172A] ${SPANS[i]}`}
            >
              {wide && (
                <>
                  <img
                    src={CARD_IMG}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-20 group-hover:opacity-35 transition-opacity duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#020617] via-[#020617]/85 to-[#020617]/40" />
                </>
              )}
              <div className="relative">
                <div className="flex items-start justify-between mb-10">
                  <span className="font-mono-tech text-xs text-slate-500">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="flex items-center gap-3">
                    <Icon size={22} className="text-[#2563EB] transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-6" />
                    <ArrowUpRight size={18} className="text-slate-600 opacity-0 -translate-x-1 translate-y-1 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 transition-all duration-500" />
                  </div>
                </div>
                <h3 className="font-display font-bold text-xl md:text-2xl tracking-tight mb-3">{s.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{s.desc}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {s.tags.map((tag, j) => (
                    <span key={j} className="font-mono-tech text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border border-white/10 text-slate-400">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
};
